import React, { useState } from 'react';
import {
  TextField,
  Checkbox,
  Button,
  FormControlLabel,
  Stack,
  Box,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import OTPInput from 'react-otp-input';
import useBludataLogoThemed from '../utility_objects/const/imageUrls';
import json2mq from 'json2mq';
import EmptyPage from '../utility_objects/EmptyPage';
import useLogoutMSAL from './useLogoutMSAL';
import localforage from 'localforage';

interface Props {
  onOtpInserted: (otp: string, remind: boolean) => void;
}

function LoginOTPRequestPage(props: Props) {
  const { logoFocusUrl } = useBludataLogoThemed();
  const { logout } = useLogoutMSAL();
  const { onOtpInserted } = props;
  const [otp, setOtp] = useState('');
  const [remind, setRemind] = useState(false);
  const otpNotValid = otp.length !== 6;

  const matchesSmall = useMediaQuery(
    json2mq({
      maxWidth: 1024,
    }),
  );

  const matchesMedium = useMediaQuery(
    json2mq({
      maxWidth: 1500,
    }),
  );

  const handleSubmit = () => {
    onOtpInserted(otp, remind);
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !otpNotValid) {
      handleSubmit();
    }
  };

  const onAnnullaClick = async () => {
    await localforage.removeItem('terminal_FOCUSCLOUD_workplaceRef');
    await localforage.removeItem('terminal_FOCUSCLOUD_workplaceName');
    logout(true, 'explicit');
  };

  return (
    <EmptyPage>
      <Paper
        elevation={0}
        sx={{
          width: 350,
          p: '40px 30px',
          zIndex: 9999,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          position: matchesSmall ? undefined : 'absolute',
          right: matchesMedium ? '10%' : '20%',
        }}
      >
        <Stack flexDirection="column" spacing={2}>
          <Stack flexDirection="row" justifyContent="center">
            <Box
              sx={{
                margin: '10px 0px',
                marginBottom: '40px',
                height: 32,
                width: 190,
                backgroundImage: `url(${logoFocusUrl})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'auto 100%',
                backgroundPosition: 'center',
              }}
            />
          </Stack>
          <Stack flexDirection="column" spacing={2}>
            <Typography fontSize="16px" textAlign="center">
              Inserisci il codice OTP fornito dalla tua app di autenticazione:
            </Typography>
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              sx={{ p: 2 }}
            >
              <OTPInput
                value={otp}
                shouldAutoFocus
                onChange={setOtp}
                numInputs={6}
                renderSeparator={<Box sx={{ width: 5 }} />}
                renderInput={(props) => {
                  props.style.width = 30;
                  return (
                    <TextField
                      {...props}
                      ref={undefined}
                      inputRef={props.ref}
                      type="text"
                      slotProps={{
                        htmlInput: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          autoComplete: 'one-time-code',
                          maxLength: 1,
                        },
                      }}
                      sx={{
                        '& input': {
                          textAlign: 'center',
                          fontSize: '18px',
                        },
                        '& input::selection': {
                          backgroundColor: 'transparent',
                        },
                      }}
                      onKeyDown={(e) => {
                        // Permetti Ctrl+V / Cmd+V per incollare
                        if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                          return;
                        }
                        if (
                          !/^[0-9]$/.test(e.key) &&
                          ![
                            'Backspace',
                            'Delete',
                            'Tab',
                            'Enter',
                            'ArrowLeft',
                            'ArrowRight',
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onKeyUp={handleKeyUp}
                    />
                  );
                }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={remind}
                  onChange={(e) => setRemind(e.target.checked)}
                />
              }
              label="Ricorda il terminale"
            />
          </Stack>
          <Stack flexDirection="row" justifyContent="end" gap={1}>
            <Button
              sx={{
                fontWeight: 400,
                height: 40,
                width: 120,
                mr: '10px',
                justifyContent: 'center',
              }}
              variant="outlined"
              color="primary"
              onClick={onAnnullaClick}
            >
              Annulla
            </Button>
            <Button
              sx={{
                fontWeight: 400,
                height: 40,
                width: 120,
                mr: '10px',
                justifyContent: 'center',
              }}
              disabled={otpNotValid}
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Conferma
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </EmptyPage>
  );
}

export default LoginOTPRequestPage;
